<template>
    <div class="animated fadeIn bd-dashboard">
        <!-- Page Header -->
        <div class="bd-header mb-4">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <h4 class="mb-0">Dashboard Analisa Borrower & Disbursement</h4>
                    <small class="text-muted">Analisa komprehensif portofolio pinjaman</small>
                </div>
                <div class="d-flex align-items-center">
                    <span v-if="lastUpdated" class="text-muted small mr-3">
                        <i class="fas fa-clock mr-1"></i> Diperbarui: {{ lastUpdated }}
                    </span>
                    <button class="btn btn-sm btn-outline-primary" @click="loadAll" :disabled="loading">
                        <i class="fas fa-sync-alt mr-1" :class="{'fa-spin': loading}"></i>
                        Refresh
                    </button>
                </div>
            </div>
        </div>

        <!-- Filter Panel -->
        <div class="bd-filter-panel mb-4">
            <div class="row align-items-end">
                <div class="col-md-3">
                    <label class="form-label-sm">Tanggal Disbursement Dari</label>
                    <input type="date" class="form-control form-control-sm" v-model="filter.date_from">
                </div>
                <div class="col-md-3">
                    <label class="form-label-sm">Sampai</label>
                    <input type="date" class="form-control form-control-sm" v-model="filter.date_to">
                </div>
                <div class="col-md-3">
                    <label class="form-label-sm">Project (pjxkey)</label>
                    <select class="form-control form-control-sm" v-model="filter.pjxkey">
                        <option value="">Semua Project</option>
                        <option v-for="p in pjxkeyOptions" :key="p" :value="p">{{ p }}</option>
                    </select>
                </div>
                <div class="col-md-3">
                    <button class="btn btn-primary btn-sm mr-2" @click="loadAll" :disabled="loading">
                        <i class="fas fa-filter mr-1"></i> Terapkan
                    </button>
                    <button class="btn btn-secondary btn-sm" @click="resetFilter">
                        <i class="fas fa-undo mr-1"></i> Reset
                    </button>
                </div>
            </div>
        </div>

        <!-- KPI Cards -->
        <div class="row mb-4" v-if="summary">
            <div class="col-sm-6 col-lg-2">
                <div class="bd-kpi-card bd-kpi-blue">
                    <div class="bd-kpi-icon"><i class="fas fa-users"></i></div>
                    <div class="bd-kpi-value">{{ fmt(summary.total_borrower) }}</div>
                    <div class="bd-kpi-label">Total Borrower</div>
                    <div class="bd-kpi-sub">{{ fmt(summary.total_loan) }} transaksi</div>
                </div>
            </div>
            <div class="col-sm-6 col-lg-2">
                <div class="bd-kpi-card bd-kpi-green">
                    <div class="bd-kpi-icon"><i class="fas fa-hand-holding-usd"></i></div>
                    <div class="bd-kpi-value">{{ fmtBillion(summary.total_loan_amount) }}</div>
                    <div class="bd-kpi-label">Total Pokok Pinjaman</div>
                    <div class="bd-kpi-sub">Net: {{ fmtBillion(summary.total_net_disbursement) }}</div>
                </div>
            </div>
            <div class="col-sm-6 col-lg-2">
                <div class="bd-kpi-card bd-kpi-orange">
                    <div class="bd-kpi-icon"><i class="fas fa-balance-scale"></i></div>
                    <div class="bd-kpi-value">{{ fmtBillion(summary.total_outstanding) }}</div>
                    <div class="bd-kpi-label">Total Outstanding</div>
                    <div class="bd-kpi-sub">{{ fmtPct(summary.total_outstanding, summary.total_loan_amount) }} dari pokok</div>
                </div>
            </div>
            <div class="col-sm-6 col-lg-2">
                <div class="bd-kpi-card bd-kpi-teal">
                    <div class="bd-kpi-icon"><i class="fas fa-check-circle"></i></div>
                    <div class="bd-kpi-value">{{ fmtBillion(summary.total_settled) }}</div>
                    <div class="bd-kpi-label">Total Terbayar</div>
                    <div class="bd-kpi-sub">{{ fmt(summary.fully_paid_count) }} lunas penuh</div>
                </div>
            </div>
            <div class="col-sm-6 col-lg-2">
                <div class="bd-kpi-card" :class="summary.dpd_plus_count > 0 ? 'bd-kpi-red' : 'bd-kpi-teal'">
                    <div class="bd-kpi-icon"><i class="fas fa-exclamation-triangle"></i></div>
                    <div class="bd-kpi-value">{{ fmt(summary.dpd_plus_count) }}</div>
                    <div class="bd-kpi-label">Loan DPD > 0</div>
                    <div class="bd-kpi-sub">DPD >90: {{ fmt(summary.dpd_over_90) }} loan</div>
                </div>
            </div>
            <div class="col-sm-6 col-lg-2">
                <div class="bd-kpi-card" :class="summary.wo_count > 0 ? 'bd-kpi-dark' : 'bd-kpi-teal'">
                    <div class="bd-kpi-icon"><i class="fas fa-times-circle"></i></div>
                    <div class="bd-kpi-value">{{ fmt(summary.wo_count) }}</div>
                    <div class="bd-kpi-label">Write-Off</div>
                    <div class="bd-kpi-sub">{{ fmtBillion(summary.wo_amount) }}</div>
                </div>
            </div>
        </div>

        <!-- DPD Quick Summary Bar -->
        <div class="bd-dpd-bar mb-4" v-if="summary">
            <div class="d-flex align-items-center mb-1">
                <span class="font-weight-bold small mr-2">Distribusi DPD (loan count):</span>
            </div>
            <div class="d-flex">
                <div class="bd-dpd-seg bd-dpd-current" :style="{ flex: summary.total_loan - summary.dpd_plus_count }">
                    <span v-if="(summary.total_loan - summary.dpd_plus_count) > 0">
                        Current ({{ fmt(summary.total_loan - summary.dpd_plus_count) }})
                    </span>
                </div>
                <div class="bd-dpd-seg bd-dpd-1" :style="{ flex: summary.dpd_1_30 || 0 }">
                    <span v-if="summary.dpd_1_30 > 0">1-30 ({{ summary.dpd_1_30 }})</span>
                </div>
                <div class="bd-dpd-seg bd-dpd-31" :style="{ flex: summary.dpd_31_60 || 0 }">
                    <span v-if="summary.dpd_31_60 > 0">31-60 ({{ summary.dpd_31_60 }})</span>
                </div>
                <div class="bd-dpd-seg bd-dpd-61" :style="{ flex: summary.dpd_61_90 || 0 }">
                    <span v-if="summary.dpd_61_90 > 0">61-90 ({{ summary.dpd_61_90 }})</span>
                </div>
                <div class="bd-dpd-seg bd-dpd-90" :style="{ flex: summary.dpd_over_90 || 0 }">
                    <span v-if="summary.dpd_over_90 > 0">>90 ({{ summary.dpd_over_90 }})</span>
                </div>
            </div>
        </div>

        <!-- Charts Row -->
        <div class="row mb-4">
            <!-- Monthly Trend Chart -->
            <div class="col-lg-8">
                <div class="card h-100">
                    <div class="card-header bd-card-header">
                        <i class="fas fa-chart-bar mr-2"></i> Tren Bulanan Disbursement & Outstanding
                    </div>
                    <div class="card-body">
                        <canvas ref="monthlyChart" height="280"></canvas>
                    </div>
                </div>
            </div>
            <!-- DPD Distribution Chart -->
            <div class="col-lg-4">
                <div class="card h-100">
                    <div class="card-header bd-card-header">
                        <i class="fas fa-chart-pie mr-2"></i> Distribusi DPD (Outstanding)
                    </div>
                    <div class="card-body d-flex flex-column align-items-center justify-content-center">
                        <canvas ref="dpdChart" height="240"></canvas>
                        <div class="mt-2 w-100">
                            <div v-for="item in dpdData" :key="item.dpd_bucket" class="d-flex justify-content-between small mb-1">
                                <span>
                                    <i class="fas fa-circle mr-1" :style="{ color: dpdColor(item.dpd_bucket) }"></i>
                                    {{ item.dpd_bucket }}
                                </span>
                                <span class="font-weight-bold">{{ fmtBillion(item.total_outstanding) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Status & Product Row -->
        <div class="row mb-4">
            <!-- Status Breakdown -->
            <div class="col-lg-6">
                <div class="card h-100">
                    <div class="card-header bd-card-header">
                        <i class="fas fa-list-alt mr-2"></i> Breakdown Status Disbursement
                    </div>
                    <div class="card-body p-0">
                        <div class="table-responsive">
                            <table class="table table-sm table-hover mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th>Status</th>
                                        <th class="text-right">Loan</th>
                                        <th class="text-right">Borrower</th>
                                        <th class="text-right">Outstanding</th>
                                        <th class="text-right">Avg DPD</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in statusData" :key="item.status">
                                        <td>
                                            <span class="badge" :class="statusBadge(item.status)">{{ item.status }}</span>
                                        </td>
                                        <td class="text-right">{{ fmt(item.loan_count) }}</td>
                                        <td class="text-right">{{ fmt(item.borrower_count) }}</td>
                                        <td class="text-right bd-currency">{{ fmtBillion(item.total_outstanding) }}</td>
                                        <td class="text-right">
                                            <span :class="item.avg_dpd > 30 ? 'text-danger' : item.avg_dpd > 0 ? 'text-warning' : 'text-success'">
                                                {{ parseFloat(item.avg_dpd || 0).toFixed(1) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot v-if="statusData.length">
                                    <tr class="font-weight-bold bg-light">
                                        <td>TOTAL</td>
                                        <td class="text-right">{{ fmt(statusData.reduce((s, r) => s + parseInt(r.loan_count || 0), 0)) }}</td>
                                        <td class="text-right"></td>
                                        <td class="text-right bd-currency">{{ fmtBillion(statusData.reduce((s, r) => s + parseFloat(r.total_outstanding || 0), 0)) }}</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Product Breakdown -->
            <div class="col-lg-6">
                <div class="card h-100">
                    <div class="card-header bd-card-header">
                        <i class="fas fa-tags mr-2"></i> Breakdown per Produk
                    </div>
                    <div class="card-body p-0">
                        <div class="table-responsive">
                            <table class="table table-sm table-hover mb-0">
                                <thead class="thead-light">
                                    <tr>
                                        <th>Produk</th>
                                        <th class="text-right">Loan</th>
                                        <th class="text-right">Total Pokok</th>
                                        <th class="text-right">Outstanding</th>
                                        <th class="text-right">Avg Tenor</th>
                                        <th class="text-right">Avg DPD</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="item in productData" :key="item.product_name">
                                        <td><small>{{ item.product_name }}</small></td>
                                        <td class="text-right">{{ fmt(item.loan_count) }}</td>
                                        <td class="text-right bd-currency">{{ fmtBillion(item.total_loan_amount) }}</td>
                                        <td class="text-right bd-currency">{{ fmtBillion(item.total_outstanding) }}</td>
                                        <td class="text-right">{{ parseFloat(item.avg_tenor || 0).toFixed(0) }}m</td>
                                        <td class="text-right">
                                            <span :class="item.avg_dpd > 30 ? 'text-danger' : item.avg_dpd > 0 ? 'text-warning' : 'text-success'">
                                                {{ parseFloat(item.avg_dpd || 0).toFixed(1) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Top Borrowers & Delinquent Tabs -->
        <div class="card mb-4">
            <div class="card-header bd-card-header p-0">
                <ul class="nav nav-tabs bd-tabs" role="tablist">
                    <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'top' }" href="#" @click.prevent="activeTab = 'top'">
                            <i class="fas fa-trophy mr-1"></i> Top Borrowers by Outstanding
                        </a>
                    </li>
                    <li class="nav-item">
                        <a class="nav-link" :class="{ active: activeTab === 'delinquent' }" href="#" @click.prevent="activeTab = 'delinquent'">
                            <i class="fas fa-exclamation-circle mr-1 text-danger"></i> Delinquent (DPD > 0)
                            <span v-if="summary && summary.dpd_plus_count > 0" class="badge badge-danger ml-1">{{ summary.dpd_plus_count }}</span>
                        </a>
                    </li>
                </ul>
            </div>
            <div class="card-body p-0">
                <!-- Top Borrowers Tab -->
                <div v-if="activeTab === 'top'">
                    <div class="table-responsive">
                        <table class="table table-sm table-hover mb-0">
                            <thead class="thead-light">
                                <tr>
                                    <th>#</th>
                                    <th>Nama Borrower</th>
                                    <th>Customer ID</th>
                                    <th class="text-right">Loan</th>
                                    <th class="text-right">Total Pokok</th>
                                    <th class="text-right">Outstanding</th>
                                    <th class="text-right">Terbayar</th>
                                    <th class="text-right">Max DPD</th>
                                    <th class="text-center">Risk Grade</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in topBorrowers" :key="item.borrowerKey">
                                    <td class="text-muted">{{ idx + 1 }}</td>
                                    <td class="font-weight-bold">{{ item.debtorFullName || '-' }}</td>
                                    <td><small class="text-muted">{{ item.customerID }}</small></td>
                                    <td class="text-right">{{ item.loan_count }}</td>
                                    <td class="text-right bd-currency">{{ fmtBillion(item.total_loan_amount) }}</td>
                                    <td class="text-right bd-currency font-weight-bold" :class="parseFloat(item.total_outstanding) > 0 ? 'text-danger' : 'text-success'">
                                        {{ fmtBillion(item.total_outstanding) }}
                                    </td>
                                    <td class="text-right bd-currency text-success">{{ fmtBillion(item.total_settled) }}</td>
                                    <td class="text-right">
                                        <span class="badge" :class="dpdBadge(item.max_dpd)">{{ item.max_dpd }}</span>
                                    </td>
                                    <td class="text-center">
                                        <span v-if="item.riskGrade" class="badge badge-secondary">{{ item.riskGrade }}</span>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                </tr>
                                <tr v-if="!topBorrowers.length">
                                    <td colspan="9" class="text-center text-muted py-3">Tidak ada data</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Delinquent Tab -->
                <div v-if="activeTab === 'delinquent'">
                    <div class="p-2 bd-filter-panel border-bottom">
                        <div class="d-flex align-items-center">
                            <label class="mb-0 mr-2 small">Min DPD:</label>
                            <select class="form-control form-control-sm w-auto mr-2" v-model="delinquentMinDpd">
                                <option value="1">1+</option>
                                <option value="31">31+</option>
                                <option value="61">61+</option>
                                <option value="91">91+</option>
                            </select>
                            <button class="btn btn-sm btn-primary" @click="loadDelinquent">
                                <i class="fas fa-search mr-1"></i> Cari
                            </button>
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm table-hover mb-0">
                            <thead class="thead-light">
                                <tr>
                                    <th>#</th>
                                    <th>Loan ID</th>
                                    <th>Nama Borrower</th>
                                    <th>No HP</th>
                                    <th>Produk</th>
                                    <th>Tgl Disb</th>
                                    <th>Jatuh Tempo</th>
                                    <th class="text-right">Outstanding</th>
                                    <th class="text-center">DPD</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in delinquentData" :key="item.disbursementTransactionId">
                                    <td class="text-muted">{{ idx + 1 }}</td>
                                    <td><small>{{ item.loan_id }}</small></td>
                                    <td class="font-weight-bold">{{ item.debtorFullName || '-' }}</td>
                                    <td><small>{{ item.mobilePhoneNumber || '-' }}</small></td>
                                    <td><small>{{ item.product_name || '-' }}</small></td>
                                    <td>{{ fmtDate(item.disbursed_date) }}</td>
                                    <td :class="isPastDue(item.due_date) ? 'text-danger font-weight-bold' : ''">
                                        {{ fmtDate(item.due_date) }}
                                    </td>
                                    <td class="text-right bd-currency font-weight-bold text-danger">
                                        {{ fmtBillion(item.loan_outstanding) }}
                                    </td>
                                    <td class="text-center">
                                        <span class="badge" :class="dpdBadge(item.dpd)">{{ item.dpd }}</span>
                                    </td>
                                    <td>
                                        <span class="badge" :class="statusBadge(item.statusDisbursement)">{{ item.statusDisbursement }}</span>
                                    </td>
                                </tr>
                                <tr v-if="!delinquentData.length">
                                    <td colspan="10" class="text-center text-muted py-3">Tidak ada data delinquent</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading Overlay -->
        <div v-if="loading" class="bd-loading-overlay">
            <div class="bd-loading-box">
                <i class="fas fa-circle-notch fa-spin fa-2x text-primary mb-2"></i>
                <div class="text-muted small">Memuat data...</div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    Chart,
    BarController, LineController,
    BarElement, LineElement, PointElement,
    CategoryScale, LinearScale,
    Tooltip, Legend, Title,
    DoughnutController, ArcElement
} from 'chart.js';
import moment from 'moment';
import api from '../../apis/borrowerDisbursement.js';

Chart.register(
    BarController, LineController,
    BarElement, LineElement, PointElement,
    CategoryScale, LinearScale,
    Tooltip, Legend, Title,
    DoughnutController, ArcElement
);

const DPD_COLORS = {
    'Current (DPD 0)': '#28a745',
    'DPD 1-30':        '#ffc107',
    'DPD 31-60':       '#fd7e14',
    'DPD 61-90':       '#dc3545',
    'DPD >90':         '#6f42c1',
    'Unknown':         '#6c757d'
};

export default {
    name: 'BorrowerDisbursementDashboard',

    data() {
        return {
            loading: false,
            lastUpdated: null,
            activeTab: 'top',
            filter: {
                date_from: '',
                date_to: '',
                pjxkey: ''
            },
            delinquentMinDpd: '1',
            pjxkeyOptions: [],
            summary: null,
            monthlyData: [],
            dpdData: [],
            statusData: [],
            productData: [],
            topBorrowers: [],
            delinquentData: [],
            monthlyChart: null,
            dpdChart: null
        };
    },

    async mounted() {
        await this.loadPjxkeyOptions();
        await this.loadAll();
    },

    beforeDestroy() {
        if (this.monthlyChart) this.monthlyChart.destroy();
        if (this.dpdChart) this.dpdChart.destroy();
    },

    methods: {
        async loadPjxkeyOptions() {
            try {
                const resp = await api.pjxkeyList();
                this.pjxkeyOptions = resp.data || [];
            } catch (e) {
                console.error(e);
            }
        },

        async loadAll() {
            this.loading = true;
            try {
                const f = this.filter;
                const [summaryResp, monthlyResp, dpdResp, statusResp, productResp, topResp] = await Promise.all([
                    api.summary(f),
                    api.monthlyTrend(f),
                    api.dpdDistribution(f),
                    api.statusBreakdown(f),
                    api.productBreakdown(f),
                    api.topBorrowers(f)
                ]);
                this.summary     = summaryResp.data;
                this.monthlyData = monthlyResp.data || [];
                this.dpdData     = dpdResp.data || [];
                this.statusData  = statusResp.data || [];
                this.productData = productResp.data || [];
                this.topBorrowers = topResp.data || [];
                this.lastUpdated = moment().format('DD MMM YYYY HH:mm');

                this.$nextTick(() => {
                    this.renderMonthlyChart();
                    this.renderDpdChart();
                });

                if (this.activeTab === 'delinquent') {
                    await this.loadDelinquent();
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },

        async loadDelinquent() {
            try {
                const resp = await api.delinquent({ ...this.filter, min_dpd: this.delinquentMinDpd, limit: 100 });
                this.delinquentData = resp.data || [];
            } catch (e) {
                console.error(e);
            }
        },

        resetFilter() {
            this.filter = { date_from: '', date_to: '', pjxkey: '' };
            this.loadAll();
        },

        renderMonthlyChart() {
            if (this.monthlyChart) this.monthlyChart.destroy();
            const canvas = this.$refs.monthlyChart;
            if (!canvas || !this.monthlyData.length) return;

            const labels = this.monthlyData.map(r => r.month);
            const disbursement = this.monthlyData.map(r => parseFloat(r.total_loan_amount || 0) / 1e9);
            const outstanding  = this.monthlyData.map(r => parseFloat(r.total_outstanding || 0) / 1e9);
            const settled      = this.monthlyData.map(r => parseFloat(r.total_settled || 0) / 1e9);

            this.monthlyChart = new Chart(canvas, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Total Pokok (Rp M)',
                            backgroundColor: 'rgba(54, 162, 235, 0.7)',
                            data: disbursement,
                            order: 2
                        },
                        {
                            label: 'Outstanding (Rp M)',
                            backgroundColor: 'rgba(255, 99, 132, 0.7)',
                            data: outstanding,
                            order: 2
                        },
                        {
                            label: 'Terbayar (Rp M)',
                            type: 'line',
                            borderColor: '#28a745',
                            backgroundColor: 'rgba(40, 167, 69, 0.1)',
                            data: settled,
                            fill: true,
                            tension: 0.3,
                            pointRadius: 4,
                            order: 1
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { position: 'top' },
                        tooltip: {
                            callbacks: {
                                label: ctx => `${ctx.dataset.label}: ${this.fmtNum(ctx.raw)} M`
                            }
                        }
                    },
                    scales: {
                        y: {
                            title: { display: true, text: 'Rp Miliar' },
                            ticks: { callback: v => `${v}M` }
                        }
                    }
                }
            });
        },

        renderDpdChart() {
            if (this.dpdChart) this.dpdChart.destroy();
            const canvas = this.$refs.dpdChart;
            if (!canvas || !this.dpdData.length) return;

            const filtered = this.dpdData.filter(r => parseFloat(r.total_outstanding || 0) > 0);
            if (!filtered.length) return;

            this.dpdChart = new Chart(canvas, {
                type: 'doughnut',
                data: {
                    labels: filtered.map(r => r.dpd_bucket),
                    datasets: [{
                        data: filtered.map(r => parseFloat(r.total_outstanding || 0) / 1e9),
                        backgroundColor: filtered.map(r => DPD_COLORS[r.dpd_bucket] || '#999'),
                        hoverOffset: 6
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '60%',
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: ctx => `${ctx.label}: ${this.fmtNum(ctx.raw)} M`
                            }
                        }
                    }
                }
            });
        },

        // --- Format helpers ---
        fmt(val) {
            return Number(val || 0).toLocaleString('id-ID');
        },
        fmtNum(val, dec = 2) {
            return parseFloat(val || 0).toFixed(dec);
        },
        fmtBillion(val) {
            const n = parseFloat(val || 0);
            if (Math.abs(n) >= 1e9)  return `${(n / 1e9).toFixed(2)} M`;
            if (Math.abs(n) >= 1e6)  return `${(n / 1e6).toFixed(2)} jt`;
            if (Math.abs(n) >= 1e3)  return `${(n / 1e3).toFixed(1)} rb`;
            return n.toLocaleString('id-ID', { minimumFractionDigits: 0 });
        },
        fmtPct(num, den) {
            if (!den || parseFloat(den) === 0) return '0%';
            return `${((parseFloat(num) / parseFloat(den)) * 100).toFixed(1)}%`;
        },
        fmtDate(val) {
            if (!val) return '-';
            return moment(val).format('DD MMM YYYY');
        },
        isPastDue(dateVal) {
            if (!dateVal) return false;
            return moment(dateVal).isBefore(moment(), 'day');
        },
        dpdColor(bucket) {
            return DPD_COLORS[bucket] || '#999';
        },
        dpdBadge(dpd) {
            const d = parseInt(dpd || 0);
            if (d === 0)  return 'badge-success';
            if (d <= 30)  return 'badge-warning';
            if (d <= 60)  return 'badge-orange';
            if (d <= 90)  return 'badge-danger';
            return 'badge-dark';
        },
        statusBadge(status) {
            const map = {
                'ACTIVE':       'badge-success',
                'PAID':         'badge-primary',
                'FULLYPAID':    'badge-primary',
                'NEEDCONFIRM':  'badge-warning',
                'PENDING':      'badge-warning',
                'CANCELLED':    'badge-secondary',
                'WRITEOFF':     'badge-dark',
                'WO':           'badge-dark'
            };
            return map[(status || '').toUpperCase()] || 'badge-secondary';
        }
    }
};
</script>

<style scoped>
.bd-dashboard {
    position: relative;
    padding-bottom: 2rem;
}

/* Header */
.bd-header {
    padding: 1rem 0 0.5rem;
    border-bottom: 2px solid #e9ecef;
}
.bd-header h4 {
    font-weight: 700;
    color: #343a40;
}

/* Filter Panel */
.bd-filter-panel {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 1rem;
}
.form-label-sm {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    margin-bottom: 0.25rem;
    display: block;
}

/* KPI Cards */
.bd-kpi-card {
    border-radius: 10px;
    padding: 1rem;
    margin-bottom: 1rem;
    color: white;
    position: relative;
    overflow: hidden;
}
.bd-kpi-card::before {
    content: '';
    position: absolute;
    top: -15px; right: -15px;
    width: 70px; height: 70px;
    background: rgba(255,255,255,0.1);
    border-radius: 50%;
}
.bd-kpi-blue   { background: linear-gradient(135deg, #1e3c72, #2a69ac); }
.bd-kpi-green  { background: linear-gradient(135deg, #11998e, #38ef7d); }
.bd-kpi-orange { background: linear-gradient(135deg, #f7971e, #ffd200); }
.bd-kpi-teal   { background: linear-gradient(135deg, #11998e, #38ef7d); }
.bd-kpi-red    { background: linear-gradient(135deg, #c0392b, #e74c3c); }
.bd-kpi-dark   { background: linear-gradient(135deg, #373b44, #4286f4); }

.bd-kpi-icon { font-size: 1.5rem; opacity: 0.7; margin-bottom: 0.4rem; }
.bd-kpi-value { font-size: 1.4rem; font-weight: 800; line-height: 1; }
.bd-kpi-label { font-size: 0.72rem; text-transform: uppercase; opacity: 0.85; margin-top: 0.25rem; }
.bd-kpi-sub   { font-size: 0.7rem; opacity: 0.75; margin-top: 0.15rem; }

/* DPD Bar */
.bd-dpd-bar {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 0.75rem 1rem;
}
.bd-dpd-bar .d-flex:last-child {
    height: 28px;
    border-radius: 4px;
    overflow: hidden;
}
.bd-dpd-seg {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    font-weight: 700;
    color: white;
    min-width: 0;
    overflow: hidden;
    transition: flex 0.4s ease;
}
.bd-dpd-current { background: #28a745; }
.bd-dpd-1       { background: #ffc107; }
.bd-dpd-31      { background: #fd7e14; }
.bd-dpd-61      { background: #dc3545; }
.bd-dpd-90      { background: #6f42c1; }

/* Card */
.card {
    border: none;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    border-radius: 8px;
    margin-bottom: 1rem;
}
.bd-card-header {
    background: linear-gradient(90deg, #2c3e50, #3d5a80);
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
    border-radius: 8px 8px 0 0 !important;
    padding: 0.6rem 1rem;
}

/* Tabs */
.bd-tabs {
    border-bottom: none;
    padding: 0 0.5rem;
}
.bd-tabs .nav-link {
    color: rgba(255,255,255,0.7);
    border: none;
    border-bottom: 3px solid transparent;
    border-radius: 0;
    padding: 0.6rem 1rem;
    font-size: 0.875rem;
    background: transparent;
}
.bd-tabs .nav-link.active {
    color: #fff;
    border-bottom-color: #fff;
    background: transparent;
    font-weight: 600;
}
.bd-tabs .nav-link:hover {
    color: #fff;
}

/* Table */
.bd-currency {
    font-family: 'Courier New', monospace;
}
.table th {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #6c757d;
    white-space: nowrap;
}
.table td {
    font-size: 0.82rem;
    vertical-align: middle;
}

/* Loading */
.bd-loading-overlay {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: rgba(255,255,255,0.6);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
}
.bd-loading-box {
    text-align: center;
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

/* Badge orange (tidak ada di Bootstrap 4) */
.badge-orange {
    background-color: #fd7e14;
    color: white;
}
</style>
